import { useEffect } from 'react';
import { MapPin, Phone, Mail, ExternalLink, Award, BookOpen } from 'lucide-react';
import { getInitials } from '@/lib/utils';

// ─── Tipografías disponibles ──────────────────────────────────────────────────
export const TIPOGRAFIAS = {
  moderna: {
    label: 'Moderna',
    heading: 'Inter, system-ui, sans-serif',
    body: 'Inter, system-ui, sans-serif',
    googleFonts: null,
  },
  clasica: {
    label: 'Clásica',
    heading: '"Merriweather", Georgia, serif',
    body: '"Open Sans", sans-serif',
    googleFonts: 'Merriweather:wght@700&family=Open+Sans',
  },
  minimalista: {
    label: 'Minimalista',
    heading: '"Space Grotesk", system-ui, sans-serif',
    body: 'Inter, system-ui, sans-serif',
    googleFonts: 'Space+Grotesk:wght@500,700',
  },
  elegante: {
    label: 'Elegante',
    heading: '"Playfair Display", Georgia, serif',
    body: '"Lato", sans-serif',
    googleFonts: 'Playfair+Display:wght@700&family=Lato',
  },
} as const;

export type TipografiaKey = keyof typeof TIPOGRAFIAS;

export interface WebConfig {
  colorAcento: string;
  fondoHeroUrl?: string;
  tipografia: TipografiaKey;
  textoAdicional?: string;
  ctaTexto?: string;
  ctaUrl?: string;
}

export const DEFAULT_WEB_CONFIG: WebConfig = {
  colorAcento: '#0e85bf',
  tipografia: 'moderna',
  ctaTexto: 'Agendar cita',
};

interface Props {
  medico: any;
  config: WebConfig;
  /** Cuando es true se muestra en un iframe simulado sin scroll externo */
  isPreview?: boolean;
}

function hexToRgb(hex: string) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r} ${g} ${b}`;
}

export function WebTemplatePreview({ medico, config, isPreview = false }: Props) {
  const tipografia = TIPOGRAFIAS[config.tipografia] ?? TIPOGRAFIAS.moderna;
  const acento = config.colorAcento || '#0e85bf';
  const acentoRgb = hexToRgb(acento);

  // Inyectar Google Fonts cuando cambia la tipografía
  useEffect(() => {
    if (!tipografia.googleFonts) return;
    const id = `gf-${config.tipografia}`;
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${tipografia.googleFonts}&display=swap`;
    document.head.appendChild(link);
  }, [config.tipografia, tipografia.googleFonts]);

  const tags = medico?.subespecialidades
    ? medico.subespecialidades.split(',').map((s: string) => s.trim()).filter(Boolean)
    : [];

  const idiomasList = medico?.idiomas
    ? medico.idiomas.split(',').map((s: string) => s.trim()).filter(Boolean)
    : [];

  const mapsUrl = medico?.clinica?.direccion
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        [medico.clinica.direccion, medico.clinica.ciudad, medico.clinica.estado]
          .filter(Boolean)
          .join(', ')
      )}`
    : null;

  return (
    <div
      style={{ fontFamily: tipografia.body, color: '#1a1a2e' }}
      className={isPreview ? 'text-[13px]' : 'text-base'}
    >
      {/* ── CSS custom properties para el color de acento ── */}
      <style>{`
        .tpl-acento { background-color: ${acento}; }
        .tpl-acento-text { color: ${acento}; }
        .tpl-acento-border { border-color: ${acento}; }
        .tpl-acento-light { background-color: rgb(${acentoRgb} / 0.08); }
        .tpl-btn { background-color: ${acento}; color: white; }
        .tpl-btn:hover { opacity: 0.9; }
      `}</style>

      {/* ─────────────────────────────────────────────────────────────────────
          SECCIÓN 1 — HERO
      ───────────────────────────────────────────────────────────────────── */}
      <section
        className="relative"
        style={{
          background: config.fondoHeroUrl
            ? `linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.35) 100%), url(${config.fondoHeroUrl}) center/cover no-repeat`
            : `linear-gradient(135deg, ${acento} 0%, ${acento}cc 60%, #0a1628 100%)`,
          padding: isPreview ? '2rem 1.5rem' : '5rem 2rem',
        }}
      >
        <div className="max-w-3xl mx-auto flex flex-col items-center text-center gap-5">
          {/* Foto */}
          <div
            className="rounded-full overflow-hidden border-4 border-white/30 shadow-xl flex-shrink-0"
            style={{ width: isPreview ? 72 : 120, height: isPreview ? 72 : 120 }}
          >
            {medico?.usuario?.fotoUrl ? (
              <img src={medico.usuario.fotoUrl} alt={medico.nombre} className="w-full h-full object-cover" />
            ) : (
              <div
                className="w-full h-full flex items-center justify-center text-white font-bold"
                style={{ backgroundColor: `${acento}99`, fontSize: isPreview ? '1.25rem' : '2rem' }}
              >
                {getInitials(medico?.nombre ?? 'M')}
              </div>
            )}
          </div>

          {/* Nombre y especialidad */}
          <div>
            <h1
              className="text-white font-bold"
              style={{
                fontFamily: tipografia.heading,
                fontSize: isPreview ? '1.2rem' : '2.5rem',
                lineHeight: 1.15,
              }}
            >
              Dr. {medico?.nombre}
            </h1>
            {medico?.especialidad?.nombre && (
              <p
                className="mt-1 font-medium"
                style={{ color: 'rgba(255,255,255,0.85)', fontSize: isPreview ? '0.75rem' : '1.1rem' }}
              >
                {medico.especialidad.nombre}
              </p>
            )}
            {medico?.aniosExperiencia && (
              <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: isPreview ? '0.65rem' : '0.9rem', marginTop: 6 }}>
                {medico.aniosExperiencia} años de experiencia
              </p>
            )}
          </div>

          {/* CTA */}
          {config.ctaTexto && (
            <a
              href={config.ctaUrl || '#'}
              target={config.ctaUrl ? '_blank' : undefined}
              rel="noopener noreferrer"
              className="tpl-btn font-semibold rounded-full shadow-lg inline-block"
              style={{
                padding: isPreview ? '0.4rem 1.2rem' : '0.85rem 2.5rem',
                fontSize: isPreview ? '0.7rem' : '1rem',
                textDecoration: 'none',
              }}
            >
              {config.ctaTexto}
            </a>
          )}
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────────────────────
          SECCIÓN 2 — SOBRE MÍ
      ───────────────────────────────────────────────────────────────────── */}
      {(medico?.bio || config.textoAdicional) && (
        <section style={{ padding: isPreview ? '1.5rem' : '4rem 2rem', background: '#ffffff' }}>
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-2 mb-5">
              <BookOpen
                size={isPreview ? 14 : 20}
                style={{ color: acento }}
              />
              <h2
                className="font-bold"
                style={{
                  fontFamily: tipografia.heading,
                  fontSize: isPreview ? '0.85rem' : '1.6rem',
                  color: acento,
                }}
              >
                Sobre mí
              </h2>
            </div>

            <div
              className="grid gap-6"
              style={{ gridTemplateColumns: medico?.aniosExperiencia || medico?.cedulaProfesional ? '1fr auto' : '1fr' }}
            >
              <div>
                {medico?.bio && (
                  <p
                    style={{
                      color: '#374151',
                      lineHeight: 1.7,
                      fontSize: isPreview ? '0.72rem' : '1rem',
                      marginBottom: config.textoAdicional ? '1rem' : 0,
                    }}
                  >
                    {medico.bio}
                  </p>
                )}
                {config.textoAdicional && (
                  <p
                    style={{
                      color: '#6b7280',
                      lineHeight: 1.7,
                      fontSize: isPreview ? '0.7rem' : '0.95rem',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {config.textoAdicional}
                  </p>
                )}
              </div>

              {/* Panel lateral de datos */}
              {(medico?.aniosExperiencia || medico?.cedulaProfesional || idiomasList.length > 0) && (
                <div
                  className="tpl-acento-light rounded-2xl flex-shrink-0"
                  style={{ padding: isPreview ? '0.75rem' : '1.5rem', minWidth: isPreview ? 120 : 200 }}
                >
                  {medico.aniosExperiencia && (
                    <div className="mb-3">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <Award size={isPreview ? 11 : 16} style={{ color: acento }} />
                        <span style={{ fontFamily: tipografia.heading, fontWeight: 700, fontSize: isPreview ? '0.65rem' : '0.85rem', color: acento }}>
                          Experiencia
                        </span>
                      </div>
                      <p style={{ fontSize: isPreview ? '0.65rem' : '0.9rem', color: '#374151' }}>
                        {medico.aniosExperiencia} años
                      </p>
                    </div>
                  )}
                  {medico.cedulaProfesional && (
                    <div className="mb-3">
                      <span style={{ fontFamily: tipografia.heading, fontWeight: 700, fontSize: isPreview ? '0.65rem' : '0.85rem', color: acento, display: 'block', marginBottom: 2 }}>
                        Cédula
                      </span>
                      <p style={{ fontSize: isPreview ? '0.65rem' : '0.9rem', color: '#374151' }}>
                        {medico.cedulaProfesional}
                      </p>
                    </div>
                  )}
                  {idiomasList.length > 0 && (
                    <div>
                      <span style={{ fontFamily: tipografia.heading, fontWeight: 700, fontSize: isPreview ? '0.65rem' : '0.85rem', color: acento, display: 'block', marginBottom: 4 }}>
                        Idiomas
                      </span>
                      <div className="flex flex-col gap-1">
                        {idiomasList.map((i: string) => (
                          <span key={i} style={{ fontSize: isPreview ? '0.62rem' : '0.85rem', color: '#374151' }}>{i}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ─────────────────────────────────────────────────────────────────────
          SECCIÓN 3 — SERVICIOS / SUBESPECIALIDADES
      ───────────────────────────────────────────────────────────────────── */}
      {tags.length > 0 && (
        <section style={{ padding: isPreview ? '1.25rem 1.5rem' : '4rem 2rem', background: '#f9fafb' }}>
          <div className="max-w-3xl mx-auto">
            <h2
              className="font-bold mb-5"
              style={{
                fontFamily: tipografia.heading,
                fontSize: isPreview ? '0.85rem' : '1.6rem',
                color: acento,
              }}
            >
              Servicios y especialidades
            </h2>
            <div className="flex flex-wrap gap-2">
              {tags.map((tag: string) => (
                <span
                  key={tag}
                  className="tpl-acento-light tpl-acento-text font-medium rounded-full"
                  style={{
                    padding: isPreview ? '0.25rem 0.75rem' : '0.6rem 1.4rem',
                    fontSize: isPreview ? '0.65rem' : '0.95rem',
                    border: `1px solid ${acento}33`,
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─────────────────────────────────────────────────────────────────────
          SECCIÓN 4 — CLÍNICA Y CONTACTO
      ───────────────────────────────────────────────────────────────────── */}
      {medico?.clinica && (
        <section style={{ padding: isPreview ? '1.25rem 1.5rem' : '4rem 2rem', background: '#ffffff' }}>
          <div className="max-w-3xl mx-auto">
            <h2
              className="font-bold mb-5"
              style={{
                fontFamily: tipografia.heading,
                fontSize: isPreview ? '0.85rem' : '1.6rem',
                color: acento,
              }}
            >
              Dónde encontrarme
            </h2>
            <div className="rounded-2xl border" style={{ borderColor: `${acento}22`, padding: isPreview ? '0.75rem' : '2rem' }}>
              <p
                className="font-bold mb-3"
                style={{ fontFamily: tipografia.heading, fontSize: isPreview ? '0.8rem' : '1.25rem', color: '#111827' }}
              >
                {medico.clinica.nombre}
              </p>
              <div className="space-y-2.5">
                {(medico.clinica.ciudad || medico.clinica.estado || medico.clinica.direccion) && (
                  <div className="flex items-start gap-2">
                    <MapPin size={isPreview ? 11 : 16} style={{ color: acento, flexShrink: 0, marginTop: 2 }} />
                    <span style={{ fontSize: isPreview ? '0.68rem' : '0.95rem', color: '#374151', lineHeight: 1.5 }}>
                      {[medico.clinica.direccion, medico.clinica.ciudad, medico.clinica.estado].filter(Boolean).join(', ')}
                    </span>
                  </div>
                )}
                {medico.clinica.telefono && (
                  <div className="flex items-center gap-2">
                    <Phone size={isPreview ? 11 : 16} style={{ color: acento }} />
                    <a
                      href={`tel:${medico.clinica.telefono}`}
                      style={{ fontSize: isPreview ? '0.68rem' : '0.95rem', color: '#374151', textDecoration: 'none' }}
                    >
                      {medico.clinica.telefono}
                    </a>
                  </div>
                )}
                {medico.clinica.correo && (
                  <div className="flex items-center gap-2">
                    <Mail size={isPreview ? 11 : 16} style={{ color: acento }} />
                    <a
                      href={`mailto:${medico.clinica.correo}`}
                      style={{ fontSize: isPreview ? '0.68rem' : '0.95rem', color: '#374151', textDecoration: 'none' }}
                    >
                      {medico.clinica.correo}
                    </a>
                  </div>
                )}
                {mapsUrl && !isPreview && (
                  <a
                    href={mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 tpl-acento-text font-medium"
                    style={{ fontSize: '0.9rem', marginTop: 8, textDecoration: 'none' }}
                  >
                    <ExternalLink size={14} />
                    Ver en Google Maps
                  </a>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      {!isPreview && (
        <footer style={{ background: '#111827', padding: '1.5rem 2rem', textAlign: 'center' }}>
          <p style={{ color: '#9ca3af', fontSize: '0.8rem' }}>
            Perfil generado por <span style={{ fontWeight: 600, color: '#d1d5db' }}>Anik</span>
          </p>
        </footer>
      )}
    </div>
  );
}
